import {
  grantContentPermission,
  listContentPermissions,
  revokeContentPermission,
  setContentPermissionLevel,
} from '@/api/permission-api';
import {
  GrantContentPermissionInput,
  SetContentPermissionLevelInput,
} from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentKeys } from './useContentQueries';

export const permissionKeys = {
  all: ['content-permissions'] as const,
  byContent: (contentId: number) => [...permissionKeys.all, contentId] as const,
};

export const useContentPermissions = (contentId: number) =>
  useQuery({
    queryKey: permissionKeys.byContent(contentId),
    queryFn: () => listContentPermissions(contentId),
    enabled: contentId > 0,
  });

const invalidateAfterPermissionChange = (
  qc: ReturnType<typeof useQueryClient>,
  contentId: number,
) => {
  qc.invalidateQueries({ queryKey: permissionKeys.byContent(contentId) });
  qc.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
  qc.invalidateQueries({ queryKey: contentKeys.lists() });
};

export const useGrantContentPermission = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contentId: number; input: GrantContentPermissionInput }) =>
      grantContentPermission(vars.contentId, vars.input),
    onSuccess: (_, { contentId }) => invalidateAfterPermissionChange(qc, contentId),
  });
};

export const useRevokeContentPermission = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contentId: number; groupId: number }) =>
      revokeContentPermission(vars.contentId, vars.groupId),
    onSuccess: (_, { contentId }) => invalidateAfterPermissionChange(qc, contentId),
  });
};

export const useSetContentPermissionLevel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contentId: number; groupId: number; input: SetContentPermissionLevelInput }) =>
      setContentPermissionLevel(vars.contentId, vars.groupId, vars.input),
    onSuccess: (_, { contentId }) => invalidateAfterPermissionChange(qc, contentId),
  });
};
